import { ROUTES } from "@/constants/Routes";
import { ATTRIBUTION_NAMES } from "@/constants/User";
import { hasAttribution } from "@/utils/permissions";
import { useUserStore } from "@/store/useUserStore";
import { Alert, Box, Divider, Link, Typography } from "@mui/material";
import { blueGrey } from "@mui/material/colors";
import Image from "next/image";
import principles_impact from "@/assets/images/principles_impact.png";
import { ImpactIcon } from "@/components/ImpactIcon";

export function PrinciplesManagement() {
	const canManage = useUserStore(
		({ attributions, isAdmin }) =>
			(hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.SOCIO_JURIDICO) ||
			hasAttribution(attributions ?? [], ATTRIBUTION_NAMES.ETICO)) || isAdmin
	);

	return (
		<>
			<Alert severity='warning'>
				Somente administradores e especialistas podem gerenciar os princípios
			</Alert>

			<Typography variant='body2'>
				Princípios éticos são um dos requisitos para que os questionários AIA
				possam ser cadastrados!
			</Typography>

			<Typography variant='body2'>
				Os princípios éticos são vinculados às questões no momento de{" "}
				{canManage ? (
					<Link href={ROUTES.QUESTIONNAIRE.REGISTER.AIA}>
						criação dos questionários AIA
					</Link>
				) : (
					<span style={{ fontWeight: "bold" }}>
						criação dos questionários AIA
					</span>
				)}
				. Essa abordagem foi elaborada de forma que perguntas e princípios
				possam ser reaproveitados em diferentes versões de questionários.
			</Typography>

			<Typography variant='body2'>
				Alterar e remover os{" "}
				{canManage ? (
					<Link href={ROUTES.PRINCIPLE.EDIT}>princípios éticos</Link>
				) : (
					"princípios éticos"
				)}{" "}
				não influenciam nos questionários AIA já criados. Cada questionário
				contém dados isolados das demais entidades do sistema (exceto a AIA).
				Dessa forma não há perca de dados de questionários anteriores e se
				mantém a consistência no sistema para as aplicações.
			</Typography>

			<Divider />

			<Box>
				<Box sx={{ display: "flex", gap: 1, alignItems: "baseline" }}>
					<ImpactIcon sx={{ height: "1rem" }} />
					<Typography
						variant='body2'
						color={blueGrey[700]}
						fontWeight={600}
						gutterBottom
					>
						Níveis de impacto
					</Typography>
				</Box>

				<Typography
					variant='body2'
					fontSize={"0.8rem"}
					sx={{ color: blueGrey[500], paddingBottom: 1 }}
					gutterBottom
				>
					Servem como guias condicionais de ações para as organizações alinharem
					suas aplicações frentes aos princípios éticos.
				</Typography>

				<Box>
					<Image
						loading='lazy'
						height={160}
						placeholder='blur'
						src={principles_impact}
						alt='niveis de impacto'
					/>
					<Typography
						component={"p"}
						variant='caption'
						sx={{ paddingLeft: 0.5 }}
					>
						Exemplo de princípio ético com recomendações de níveis de impacto
					</Typography>
				</Box>
			</Box>

			<Typography variant='body2'>
				É importante reforçar um{" "}
				<span style={{ fontWeight: "bold" }}>detalhamento completo</span> e
				claro para cada nível de impacto dos princípios. Os níveis de impacto
				servem como referências para as organizações tomarem providências
				referentes às suas aplicações.
			</Typography>

			<Box>
				<Typography variant='body2'>
					Para o gerenciamento correto dos princípios éticos, algumas regras
					devem ser observadas:
				</Typography>
				<ul
					style={{
						margin: 0,
						paddingLeft: "1.5rem",
						paddingTop: "0.2rem",
						display: "flex",
						flexDirection: "column",
						gap: 3,
					}}
				>
					<li>
						<Typography variant='body2'>
							Princípíos devem ter nomes únicos
						</Typography>
					</li>
					<li>
						<Typography variant='body2'>
							Os três níveis de impacto (baixo, intermediário e alto) devem
							estar preenchidos com as devidas orientações
						</Typography>
					</li>
					<li>
						<Typography variant='body2'>
							Níveis de impacto não podem ter a mesma redação
						</Typography>
					</li>
				</ul>
			</Box>
			<Typography variant='body2'>
				É importante ressaltar que cada nível de impacto representa as
				orientações que o responsável pela aplicação deve executar para adequar
				a ferramenta às orientações. Portanto, é recomendável redigir de forma
				clara, objetiva e completa (técnico e organizacional) quais ações devem
				ser tomadas.
			</Typography>
		</>
	);
}
